import type { ShopifyCart, ShopifyCartItem } from '../types/servicesShopifyCart';
import type { CartUrlActionItem, CartUrlNoticeItem } from '../types/models';
import { getLineIdentity, getLineQuantities } from './lineItemProperties';

const CART_URL_ACTION_KEY = 'cart_url_action';

type CartUrlActionLine = {
  key: string;
  identity: string;
  quantity: number;
  isFreeGift: boolean;
};

type CartUrlActionRequest = {
  identity: string;
  quantity: number;
  title: string;
};

export type CartUrlActionRecord = {
  id: string;
  token: string;
  phase: 'prepared' | 'sent';
  snapshot: CartUrlActionLine[];
  requested: CartUrlActionRequest[];
};

const getQuantitiesByIdentity = (
  lines: { identity: string; quantity: number }[],
): Map<string, number> =>
  lines.reduce(
    (quantities, { identity, quantity }) =>
      quantities.set(identity, (quantities.get(identity) ?? 0) + quantity),
    new Map<string, number>(),
  );

export const createCartUrlActionRecord = (
  cart: ShopifyCart,
  urlItems: CartUrlActionItem[],
  isFreeGiftItem: (item: ShopifyCartItem) => boolean,
): CartUrlActionRecord => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  token: cart.token,
  phase: 'prepared',
  snapshot: cart.items.map((line) => ({
    key: line.key,
    identity: getLineIdentity(line.variant_id, line.properties),
    quantity: line.quantity,
    isFreeGift: isFreeGiftItem(line),
  })),
  requested: urlItems.map(({ item, title }) => ({
    identity: getLineIdentity(item.id, item.properties),
    quantity: item.quantity,
    title,
  })),
});

export const readCartUrlActionRecord = (): CartUrlActionRecord | null => {
  try {
    const storedRecord = sessionStorage.getItem(CART_URL_ACTION_KEY);
    return storedRecord ? (JSON.parse(storedRecord) as CartUrlActionRecord) : null;
  } catch {
    return null;
  }
};

export const writeCartUrlActionRecord = (record: CartUrlActionRecord): boolean => {
  try {
    sessionStorage.setItem(CART_URL_ACTION_KEY, JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
};

export const clearCartUrlActionRecord = () => {
  try {
    sessionStorage.removeItem(CART_URL_ACTION_KEY);
  } catch (error) {
    console.error('Failed to clear the cart URL action record', error);
  }
};

export const getCartUrlActionItems = (
  { snapshot, requested }: CartUrlActionRecord,
  cart: ShopifyCart,
): CartUrlNoticeItem[] => {
  const quantitiesBefore = getQuantitiesByIdentity(snapshot);
  const currentQuantities = getLineQuantities(cart.items);
  return requested.map(({ identity, quantity, title }) => ({
    title,
    requestedQuantity: quantity,
    addedQuantity: Math.min(
      quantity,
      Math.max(0, (currentQuantities.get(identity) ?? 0) - (quantitiesBefore.get(identity) ?? 0)),
    ),
  }));
};

export const isCartUrlActionComplete = (items: CartUrlNoticeItem[]): boolean =>
  items.every(({ addedQuantity, requestedQuantity }) => addedQuantity === requestedQuantity);

export const hasCartUrlActionAdditions = (items: CartUrlNoticeItem[]): boolean =>
  items.some(({ addedQuantity }) => !!addedQuantity);

export const hasUnexpectedCartChanges = (
  { snapshot, requested }: CartUrlActionRecord,
  cart: ShopifyCart,
  isFreeGiftItem: (item: ShopifyCartItem) => boolean,
): boolean => {
  const requestedQuantities = getQuantitiesByIdentity(requested);
  const quantitiesBefore = getQuantitiesByIdentity(
    snapshot.filter(({ isFreeGift }) => !isFreeGift),
  );
  const currentQuantities = getLineQuantities(cart.items.filter((item) => !isFreeGiftItem(item)));
  const identities = new Set([...quantitiesBefore.keys(), ...currentQuantities.keys()]);
  return [...identities].some((identity) => {
    const quantityBefore = quantitiesBefore.get(identity) ?? 0;
    const currentQuantity = currentQuantities.get(identity) ?? 0;
    const requestedQuantity = requestedQuantities.get(identity) ?? 0;
    return currentQuantity < quantityBefore || currentQuantity > quantityBefore + requestedQuantity;
  });
};

export const getCartUrlActionUndo = (
  { snapshot, requested }: CartUrlActionRecord,
  cart: ShopifyCart,
): Record<string, number> => {
  const lineQuantitiesBefore = new Map(snapshot.map(({ key, quantity }) => [key, quantity]));
  const quantitiesBefore = getQuantitiesByIdentity(snapshot);
  return requested.reduce<Record<string, number>>((updates, { identity, quantity }) => {
    const lines = cart.items.filter(
      (line) => getLineIdentity(line.variant_id, line.properties) === identity,
    );
    const currentQuantity = lines.reduce((total, line) => total + line.quantity, 0);
    const newLines = lines.filter(({ key }) => !lineQuantitiesBefore.has(key));
    const existingLines = lines.filter(({ key }) => lineQuantitiesBefore.has(key));
    let excessQuantity = Math.min(
      quantity,
      currentQuantity - (quantitiesBefore.get(identity) ?? 0),
    );
    [...newLines, ...existingLines].forEach(({ key, quantity: lineQuantity }) => {
      const reduction = Math.min(
        excessQuantity,
        Math.max(0, lineQuantity - (lineQuantitiesBefore.get(key) ?? 0)),
      );
      if (reduction <= 0) return;
      updates[key] = lineQuantity - reduction;
      excessQuantity -= reduction;
    });
    return updates;
  }, {});
};
